// ═══════════════════════════════════════════════════════════════════════════
// basisMarketAliases.js — the ONE map from a basis sheet's PRINTED column
// header to the source slug the history is keyed on.
//
// WHY IT HAS TO BE SHARED. Two writers put rows into
// basis_sheet_observations: the 2021-2026 backfill
// (tools/basis-history-load.js) and every sheet imported through Grain
// Marketing. grainCash.js's basisHistoryContext() gates comparability on the
// SLUG, so if the live importer slugs "P/U West" its own way while the
// backfill calls it howlett_pickup_west, every new sheet opens a fresh cohort
// beside the history instead of extending it — and 6,377 backfilled
// observations become unreachable for the one thing they exist to do.
// api/_howlett-basis.js's generic slugger does exactly that: it turns
// "Dry P/U East of Lima" into howlett_dry_p_u_east_of_lima, not
// howlett_pickup_east_lima. Hence one map, imported by both.
//
// DERIVED, NOT HAND-COPIED. Generated from the transcription package's own
// alias table and verified to reproduce the slug of all 6,332 package rows
// with zero misses (2026-08-19). The three merge decisions it encodes:
//
//   * "Geneva" / "Dlv Geneva" / "Geneva Mill" -> howlett_geneva. A MERGE, and
//     one Kevin confirmed on 2026-08-19 (same buyer). Recorded, not inferred.
//   * "Del Mar / Batavia" and "CEC / Del Mar" stay SEPARATE. Never confirmed,
//     so never merged — the same question, answered the other way for want of
//     an answer.
//   * an OLD CROP block gets its own slug wherever a delivery point prints
//     both old- and new-crop rows. On the 2022-04-01 and 2022-04-12 sheets the
//     two blocks both print Apr against Z22 and sit 77c apart; unsplit they
//     land on one key and both pass the comparability gate into one percentile.
//
// AN UNKNOWN LABEL IS NEVER GUESSED INTO A NEIGHBOUR. slugForMarket() returns
// null for a header this map has never seen, and the caller records it under a
// clearly-marked unmapped slug rather than picking whichever known label looks
// closest. A new column on a sheet is a question for a human, not a fuzzy match
// — merging it into the wrong elevator would silently corrupt that elevator's
// history, which is worse than an obviously unmapped source sitting in a list.
// ═══════════════════════════════════════════════════════════════════════════

const BASIS_MARKET_ALIASES = {
  "Avon": "howlett_avon",
  "Avon only": "howlett_avon",
  "Dlv Avon": "howlett_avon",
  "Avon & Lakeville": "howlett_avon_lakeville",
  "Dlv Avon & Lakeville": "howlett_avon_lakeville",
  "Dlv Avon/Lakeville": "howlett_avon_lakeville",
  "Avon/Lakeville/Del Mar": "howlett_avon_lakeville_del_mar_combined",
  "Dlv Avon/Lakeville/Star": "howlett_avon_lakeville_star",
  "Dlv Avon or Del Mar": "howlett_avon_or_del_mar_combined",
  "Batavia": "howlett_batavia",
  "Dlv Batavia": "howlett_batavia",
  "Dlv Batavia & TDAg": "howlett_batavia_tdag",
  "Dlv C.E.C.": "howlett_cec",
  "CEC / Del Mar": "howlett_cec_del_mar_combined",
  "Dlv C.E.C. (Dry)": "howlett_cec_dry",
  "Del Mar": "howlett_del_mar",
  "Dlv Del Mar": "howlett_del_mar",
  "Del Mar / Batavia": "howlett_del_mar_batavia",
  "Del Mar-Batavia New Crop 2022": "howlett_del_mar_batavia",
  "Del Mar Old Crop": "howlett_del_mar_old_crop",
  "General": "howlett_general_unspecified",
  "General / unspecified": "howlett_general_unspecified",
  "General New Crop 2022": "howlett_general_unspecified",
  "General New Crop 2023": "howlett_general_unspecified",
  "General Old Crop": "howlett_general_unspecified_old_crop",
  "Dlv Geneva": "howlett_geneva",
  "Geneva": "howlett_geneva",
  "Geneva Mill": "howlett_geneva",
  "Geneva Mill New Crop 2022": "howlett_geneva",
  "Geneva Mill New Crop 2023": "howlett_geneva",
  "Geneva Mill Old Crop": "howlett_geneva_old_crop",
  "Dlv Leroy": "howlett_leroy",
  "Dlv Leroy & TDAg": "howlett_leroy_tdag",
  "P/U East of CA": "howlett_pickup_east_ca",
  "Dry P/U East of Lima": "howlett_pickup_east_lima",
  "P/U East of Lima": "howlett_pickup_east_lima",
  "P/U East": "howlett_pickup_east_unspecified",
  "Dry Picked Up": "howlett_pickup_generic",
  "Picked Up": "howlett_pickup_generic",
  "Dry Picked Up West": "howlett_pickup_west",
  "P/U West": "howlett_pickup_west",
  "Picked Up West": "howlett_pickup_west",
  "P/U West of CA": "howlett_pickup_west_ca",
  "Plenish Dlv Lakeville": "plenish_lakeville_delivered",
  "Plenish Lakeville": "plenish_lakeville_delivered",
  "Plenish P/U": "plenish_pickup",
  "Plenish Picked Up": "plenish_pickup",
  "Wayne County Eggs": "wayne_county_eggs",
  "Wayne Eggs": "wayne_county_eggs",
  "Wayne Eggs New Crop": "wayne_county_eggs",
  "Feed Wheat Avon": "wheat_feed_avon",
  "Feed Wheat": "wheat_feed_generic",
  "Milling Old Crop Avon": "wheat_milling_avon",
  "Milling Old Crop": "wheat_milling_generic",

  // ── THE LIVE BOARD'S OWN SPELLINGS (added 2026-08-19) ────────────────────
  // Everything above came from the transcription package. These five come from
  // grain_basis_board itself, because the sheet abbreviates the SAME delivery
  // point differently in different commodity sections and the vision extractor
  // copies each spelling verbatim, as it is told to.
  //
  // This is not hypothetical tidying. It is already fragmenting the live
  // history: basis_calculated_daily holds Avon/Lakeville under THREE slugs —
  // howlett_avon_lakeville (445 rows), howlett_avon_lkville (394) and
  // howlett_avon_lakevlle (231) — and LeRoy under two (howlett_leroy_td_ag 342,
  // howlett_leroy_tdag 52), across the six weeks it has been running. One
  // elevator's basis is being scored as four unrelated markets.
  //
  // Each line below is an ABBREVIATION of the mapped label, not a judgement
  // about two differently-named buyers: "Lkville"/"Lakevlle" are Lakeville,
  // "TD Ag" is TDAg. That is a reading of a printed contraction, and it is the
  // one thing here worth a second pair of eyes — contrast Del Mar / Batavia vs
  // CEC / Del Mar, two genuinely different NAMES, still deliberately unmerged.
  //
  // Deliberately NOT aliased: "Avon & Lakeville Batavia". That is two adjacent
  // column headers concatenated — the exact extraction error the import prompt
  // warns against — so it stays unmapped and visible rather than being filed
  // under a real elevator.
  "Avon & Lkville": "howlett_avon_lakeville",
  "Avon/Lakevlle": "howlett_avon_lakeville",
  "Avon/Lakeville": "howlett_avon_lakeville",
  "Avon/Lkvlle/Star": "howlett_avon_lakeville_star",
  "LeRoy & TD Ag": "howlett_leroy_tdag",
  // Sheet 8-C (8/21/2026) prints the SAME column with the space closed up.
  // Same class as the abbreviations above — "TD Ag" is TDAg — and the space is
  // the only difference from the line above it. Until this was added the
  // column was refused (correctly, never guessed) and Woodlawn's and
  // Corcoran's targets had no board price on import day.
  "LeRoy & TDAg": "howlett_leroy_tdag",
  "Plenish Lkville": "plenish_lakeville_delivered",
};

// ── DELIVERY_TOWN_SLUGS — the TOWN a ticket names -> the board columns that
// quote it. Kevin, 2026-08-24: "Location should be Avon, or Lakeville, or
// Leroy, or batavia, or Geneva. Even though basis sheet says Avon &
// Lakeville together."
//
// He is describing the real relationship. The target sheet (Version B) has a
// checkbox per TOWN, because that is where the grower's truck goes. The basis
// sheet quotes "Avon & Lakeville" as ONE bid covering both, so a Lakeville
// ticket prices off that column — not as an approximation, that is what the
// column MEANS.
//
// EXPLICIT, NOT DERIVED, for the same reason the map above is. Token matching
// would resolve "Lakeville" against "Avon & Lakeville" and MISS the current
// sheets entirely, which spell it "Avon & Lkville" (corn) and "Avon/Lakevlle"
// (wheat). Reading a town out of a contraction is exactly the fuzzy match this
// file refuses to do anywhere else, so the answer is written down instead.
//
// Each list was derived by scanning the alias map for the town and its printed
// abbreviations, then pruned by hand: the wheat_* and plenish_* slugs are
// SEPARATE PRODUCTS (feed/milling wheat, high-oleic soy) rather than a bid for
// that town's ordinary crop, and *_old_crop is its own regime — CLAUDE.md
// records old and new crop printing the same month 77c apart.
const DELIVERY_TOWN_SLUGS = {
  "Avon":      ["howlett_avon", "howlett_avon_lakeville", "howlett_avon_lakeville_star",
                "howlett_avon_lakeville_del_mar_combined", "howlett_avon_or_del_mar_combined"],
  "Lakeville": ["howlett_avon_lakeville", "howlett_avon_lakeville_star",
                "howlett_avon_lakeville_del_mar_combined"],
  "Batavia":   ["howlett_batavia", "howlett_batavia_tdag", "howlett_del_mar_batavia"],
  "LeRoy":     ["howlett_leroy", "howlett_leroy_tdag"],
  "Geneva":    ["howlett_geneva"],
};

// The town a label names, case-insensitively; null for anything else so the
// caller can fall back to matching printed column headers.
function slugsForTown(town) {
  const key = String(town || "").trim().toLowerCase();
  if (!key) return null;
  for (const t of Object.keys(DELIVERY_TOWN_SLUGS)) {
    if (t.toLowerCase() === key) return DELIVERY_TOWN_SLUGS[t].slice();
  }
  return null;
}
// ── CANONICAL_MARKET_LABELS — the vocabulary the sheet EXTRACTOR is given.
//
// The alias map above records every spelling ever printed. This is the other
// direction: ONE label per delivery point, handed to the model so it stops
// transcribing whatever contraction that commodity's section happened to use.
// The Avon/Lakeville column alone has arrived six different ways, and each new
// one silently un-priced somebody's target until an alias was added after the
// fact. Constraining the OUTPUT ends that; an unrecognised delivery point is
// still returned verbatim and still refused, which is the doctrine intact.
const CANONICAL_MARKET_LABELS = [
  "Avon", "Avon & Lakeville", "Avon/Lkvlle/Star", "Batavia", "LeRoy & TD Ag", "Geneva",
  "Del Mar", "Dlv C.E.C.", "P/U West", "P/U East", "Dry P/U East of Lima", "Picked Up",
  "Plenish Lkville", "Plenish P/U", "Wayne County Eggs",
];
// Exact match on the trimmed printed label. Deliberately NOT fuzzy: see header.
function slugForMarket(printedLabel) {
  const key = String(printedLabel || "").trim();
  if (!key) return null;
  return BASIS_MARKET_ALIASES[key] || null;
}

// The slug an UNMAPPED label is filed under. It is deterministic (so the same
// unknown column keeps landing in the same place rather than scattering) and it
// is obviously unmapped (so it can never be mistaken for a known elevator, and
// so a review pass can list them). Nothing pools with it but itself.
function unmappedSlug(printedLabel) {
  const s = String(printedLabel || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return "unmapped_" + (s || "blank");
}

// ── legacyHowlettSlugRemap — old generic slug -> the slug it should have been.
// DERIVED from the map above by replaying api/_howlett-basis.js's own slugger
// over every printed label, so it cannot drift from the aliases and cannot be
// hand-mistyped. Used by tools/basis-source-remap.js to repair the six weeks of
// basis_calculated_daily written before the shared map existed.
//
// A slug this returns nothing for is LEFT ALONE and reported, never guessed.
// howlett_avon_lakeville_batavia is exactly that case: two adjacent column
// headers concatenated by the extractor, a defect rather than a delivery point.
const legacySlug = (s) => "howlett_" + String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
function legacyHowlettSlugRemap() {
  const out = {};
  for (const [printed, canonical] of Object.entries(BASIS_MARKET_ALIASES)) {
    const old = legacySlug(printed);
    if (old === canonical) continue;              // nothing to repair
    if (out[old] && out[old] !== canonical) {
      throw new Error(`legacy slug ${old} maps to both ${out[old]} and ${canonical}`);
    }
    out[old] = canonical;
  }
  return out;
}

module.exports = { BASIS_MARKET_ALIASES, DELIVERY_TOWN_SLUGS, CANONICAL_MARKET_LABELS,
                   slugForMarket, slugsForTown,
                   unmappedSlug, legacyHowlettSlugRemap };
